import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { X, Users, Eye, Edit3, Trash2 } from 'lucide-react';
import { MemoShare, ShareMemoData } from '../../services/memoService';
import { userService } from '../../services/userService';

interface ShareMemoModalProps {
    memoId: number;
    memoTitle: string;
    existingShares: MemoShare[];
    isOpen: boolean;
    onClose: () => void;
    onShare: (shares: ShareMemoData[]) => Promise<void>;
    onRemoveShare: (userId: number) => Promise<void>;
    onUpdateAccessLevel: (userId: number, accessLevel: 'view' | 'edit') => Promise<void>;
}

interface User {
    id: number;
    full_name: string;
    email: string;
}

export default function ShareMemoModal({
    memoId,
    memoTitle,
    existingShares,
    isOpen,
    onClose,
    onShare,
    onRemoveShare,
    onUpdateAccessLevel
}: ShareMemoModalProps) {
    const { theme } = useTheme();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<User[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<Map<number, { user: User, accessLevel: 'view' | 'edit' }>>(new Map());
    const [isSearching, setIsSearching] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Search for users
    useEffect(() => {
        const searchUsers = async () => {
            if (searchQuery.trim().length < 2) {
                setSearchResults([]);
                return;
            }

            setIsSearching(true);
            try {
                const results = await userService.searchUsers(searchQuery, 1, 10);
                setSearchResults(results);
            } catch (error) {
                console.error('Error searching users:', error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        };

        const debounceTimer = setTimeout(searchUsers, 300);
        return () => clearTimeout(debounceTimer);
    }, [searchQuery]);

    const handleAddUser = (user: User, accessLevel: 'view' | 'edit') => {
        const newSelected = new Map(selectedUsers);
        newSelected.set(user.id, { user, accessLevel });
        setSelectedUsers(newSelected);
        setSearchQuery('');
        setSearchResults([]);
    };

    const handleRemoveSelectedUser = (userId: number) => {
        const newSelected = new Map(selectedUsers);
        newSelected.delete(userId);
        setSelectedUsers(newSelected);
    };

    const handleUpdateSelectedAccessLevel = (userId: number, accessLevel: 'view' | 'edit') => {
        const newSelected = new Map(selectedUsers);
        const existing = newSelected.get(userId);
        if (existing) {
            newSelected.set(userId, { ...existing, accessLevel });
            setSelectedUsers(newSelected);
        }
    };

    const handleShare = async () => {
        if (selectedUsers.size === 0) return;

        setIsSaving(true);
        try {
            const shares: ShareMemoData[] = Array.from(selectedUsers.entries()).map(([userId, { accessLevel }]) => ({
                userId,
                accessLevel
            }));

            await onShare(shares);
            setSelectedUsers(new Map());
        } catch (error) {
            console.error('Error sharing memo:', error);
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    // Check if user is already shared
    const isAlreadyShared = (userId: number) => {
        return existingShares.some(share => share.userId === userId);
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-200"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                className={`w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl transform transition-all animate-in zoom-in-95 duration-300 ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-100'
                    }`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className={`flex items-center justify-between px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'
                    }`}>
                    <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-blue-600/20' : 'bg-blue-50'}`}>
                            <Users className={theme === 'dark' ? 'text-blue-400' : 'text-blue-600'} size={20} />
                        </div>
                        <div>
                            <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Share Memo</h2>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{memoTitle}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className={`p-2 rounded-lg transition-all hover:scale-110 ${theme === 'dark' ? 'hover:bg-gray-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                        }`}>
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className={`overflow-y-auto px-6 py-5 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`} style={{ maxHeight: 'calc(90vh - 140px)' }}>

                    {/* User Search */}
                    <div className="mb-6">
                        <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Add people</label>
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search users by name or email..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className={`w-full px-4 py-2 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            />

                            {/* Search Results */}
                            {searchResults.length > 0 && (
                                <div className={`absolute top-full left-0 right-0 mt-1 rounded-lg border shadow-lg z-10 max-h-60 overflow-y-auto ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                                    }`}>
                                    {searchResults.map(user => (
                                        <div key={user.id} className="flex items-center justify-between p-3 hover:bg-gray-100 dark:hover:bg-gray-600">
                                            <div>
                                                <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{user.full_name}</p>
                                                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</p>
                                            </div>
                                            {!isAlreadyShared(user.id) && !selectedUsers.has(user.id) && (
                                                <div className="flex space-x-2">
                                                    <button onClick={() => handleAddUser(user, 'view')} className={`px-3 py-1 rounded text-xs flex items-center space-x-1 ${theme === 'dark' ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                                                        }`}>
                                                        <Eye size={14} /><span>View</span>
                                                    </button>
                                                    <button onClick={() => handleAddUser(user, 'edit')} className="px-3 py-1 rounded text-xs bg-blue-600 hover:bg-blue-700 text-white flex items-center space-x-1">
                                                        <Edit3 size={14} /><span>Edit</span>
                                                    </button>
                                                </div>
                                            )}
                                            {(isAlreadyShared(user.id) || selectedUsers.has(user.id)) && (
                                                <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Already added</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Selected Users */}
                    {selectedUsers.size > 0 && (
                        <div className="mb-6">
                            <h3 className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>To be shared with:</h3>
                            <div className="space-y-2">
                                {Array.from(selectedUsers.entries()).map(([userId, { user, accessLevel }]) => {
                                    return (
                                        <div key={userId} className={`flex items-center justify-between p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                                            }`}>
                                            <div>
                                                <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{user.full_name}</p>
                                                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</p>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <select
                                                    value={accessLevel}
                                                    onChange={(e) => handleUpdateSelectedAccessLevel(userId, e.target.value as 'view' | 'edit')}
                                                    className={`px-2 py-1 rounded border text-sm ${theme === 'dark' ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                        }`}
                                                >
                                                    <option value="view">👁️ Can view</option>
                                                    <option value="edit">✏️ Can edit</option>
                                                </select>
                                                <button onClick={() => handleRemoveSelectedUser(userId)} className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600">
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Existing Shares */}
                    {existingShares.length > 0 && (
                        <div>
                            <h3 className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>People with access:</h3>
                            <div className="space-y-2">
                                {existingShares.map(share => (
                                    <div key={share.userId} className={`flex items-center justify-between p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                                        }`}>
                                        <div>
                                            <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{share.userName}</p>
                                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{share.userEmail}</p>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <select
                                                value={share.accessLevel}
                                                onChange={(e) => onUpdateAccessLevel(share.userId, e.target.value as 'view' | 'edit')}
                                                className={`px-2 py-1 rounded border text-sm ${theme === 'dark' ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                                                    }`}
                                            >
                                                <option value="view">👁️ Can view</option>
                                                <option value="edit">✏️ Can edit</option>
                                            </select>
                                            <button onClick={() => onRemoveShare(share.userId)} className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {existingShares.length === 0 && selectedUsers.size === 0 && (
                        <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            <Users size={48} className="mx-auto mb-2 opacity-50" />
                            <p>This memo isn't shared with anyone yet</p>
                            <p className="text-sm mt-1">Search for people to share it with</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className={`px-6 py-4 border-t flex items-center justify-between ${theme === 'dark' ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50'
                    }`}>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                        {selectedUsers.size > 0 ? `${selectedUsers.size} user(s) pending` : 'No pending shares'}
                    </p>
                    <div className="flex space-x-2">
                        <button onClick={onClose} className={`px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                            }`}>Close</button>
                        {selectedUsers.size > 0 && (
                            <button onClick={handleShare} disabled={isSaving} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
                                {isSaving ? 'Sharing...' : `Share with ${selectedUsers.size} user(s)`}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
